(() => {
  const API_ROOT = '/apps/8bp-account';
  const EXTENSION_ACCOUNT_KEYS = ['facebook_login_identifier', 'facebook_profile_url', 'facebook_profile_url_placeholder', 'update_required', 'ready_to_use', 'invalid_login_identifier', 'invalid_facebook_profile_url', 'profile_update_incomplete', 'existing_account_group', 'new_account_group', 'provide_later_group', 'provide_later_option', 'provide_later_notice', 'live_chat_sign_in', 'telegram_order_number', 'initial_submission_title', 'initial_submission_intro', 'provider_apple'];
  // Packed across extension_catalog_3..5, in this order.
  const EXTENSION_CORRECTION_KEYS = ['title', 'intro', 'submit', 'success_title', 'success_body', 'expired_title', 'expired_body', 'resolved_title', 'resolved_body', 'invalid_title', 'invalid_body', 'account_context', 'switch_type', 'contact_title', 'contact_body', 'reason_login_identifier', 'reason_password', 'reason_backup_codes', 'reason_unique_id', 'reason_facebook_profile_url', 'reason_account_type'];
  const REASON_ORDER = ['account_type', 'login_identifier', 'password', 'backup_codes', 'facebook_profile_url', 'unique_id'];
  const CHAT_URL = 'https://www.bbirdg.com/?chat';
  const TELEGRAM_URL = 'https://go.bbirdg.com/tgDM';
  let openThemeSelect = null;

  const isFacebookGmailAddress = (value) => (
    /^[^\s@]+@gmail\.com$/i.test(String(value || '').trim())
  );

  const createElement = (tag, attributes = {}, text = '') => {
    const element = document.createElement(tag);
    Object.entries(attributes).forEach(([name, value]) => {
      if (value === false || value === null || value === undefined || value === '') return;
      if (value === true) element.setAttribute(name, '');
      else element.setAttribute(name, String(value));
    });
    if (text) element.textContent = text;
    return element;
  };

  // The theme's error message (snippets/error.liquid), cloned from the form's template.
  const createErrorBox = (root) => {
    const box = root.querySelector('[data-product-input-error]')?.content?.firstElementChild?.cloneNode(true)
      || createElement('div', { class: 'product-form__error-message-wrapper', role: 'alert' });
    if (!box.querySelector('.product-form__error-message')) box.append(createElement('span', { class: 'product-form__error-message' }));
    box.hidden = true;
    return box;
  };

  const setErrorBox = (box, message) => {
    box.querySelector('.product-form__error-message').textContent = message || '';
    box.hidden = !message;
  };

  const createClearButton = (label, handler, iconTemplate = null) => {
    const button = createElement('button', {
      type: 'button',
      class: 'product-input-field__clear',
      'aria-label': label,
      title: label,
      hidden: true
    });
    const icon = iconTemplate?.content?.firstElementChild?.cloneNode(true);
    if (icon) {
      icon.setAttribute('aria-hidden', 'true');
      icon.removeAttribute('width');
      icon.removeAttribute('height');
      button.append(icon);
    } else {
      button.textContent = '×';
    }
    button.addEventListener('click', handler);
    return button;
  };

  const uniqueIdState = window.EightBpUniqueId;

  const decodeHtmlEntities = (value) => {
    if (typeof value !== 'string' || !value.includes('&')) return value;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value;
  };

  const decodeTranslationTree = (value) => {
    if (Array.isArray(value)) return value.map(decodeTranslationTree);
    if (value && typeof value === 'object') {
      return Object.fromEntries(Object.entries(value).map(([key, child]) => [key, decodeTranslationTree(child)]));
    }
    return decodeHtmlEntities(value);
  };

  // Product forms pass catalogs 1-2 (account); the correction page also passes 3-5.
  const decodeExtensionCatalog = (chunks) => {
    const parsed = (Array.isArray(chunks) ? chunks : []).map((chunk) => {
      try {
        const values = JSON.parse(decodeHtmlEntities(chunk));
        return Array.isArray(values) ? values : [];
      } catch (_) {
        return [];
      }
    });
    const pick = (keys, values) => Object.fromEntries(
      keys.map((key, index) => [key, values[index]]).filter(([, value]) => typeof value === 'string')
    );
    const [accountValues = [], profileHelp = [], ...correctionChunks] = parsed;
    const account = pick(EXTENSION_ACCOUNT_KEYS, accountValues);
    if (profileHelp.length) account.facebook_profile_help = { title: profileHelp[0], steps: profileHelp.slice(1) };
    return { account, correction: pick(EXTENSION_CORRECTION_KEYS, correctionChunks.flat()) };
  };

  // Splits a message on __CHAT__ / __TELEGRAM__ tokens into text and support links.
  const supportMessage = (element, message, labels) => {
    const links = {
      __CHAT__: createElement('a', { href: CHAT_URL, target: '_blank', rel: 'noopener' }, labels.chat),
      __TELEGRAM__: createElement('a', { href: TELEGRAM_URL, target: '_blank', rel: 'noopener' }, labels.telegram)
    };
    String(message || '').split(/(__CHAT__|__TELEGRAM__)/).forEach((part) => element.append(links[part] || part));
    return element;
  };

  const readConfig = (component) => {
    try {
      const config = JSON.parse(component.querySelector('[data-8bp-account-config]')?.textContent || '{}');
      const extensionCatalog = decodeExtensionCatalog(config.extensionCatalog);
      return {
        providers: Array.isArray(config.providers) ? config.providers.filter((provider) => provider?.key) : [],
        accountLoginUrl: typeof config.accountLoginUrl === 'string' ? config.accountLoginUrl : '',
        translations: {
          ...(config.translations && typeof config.translations === 'object' ? decodeTranslationTree(config.translations) : {}),
          ...extensionCatalog.account
        }
      };
    } catch (error) {
      return { providers: [], accountLoginUrl: '', translations: {} };
    }
  };

  const appendTokenizedText = (element, message, token, replacement) => {
    const index = message.indexOf(token);
    if (index < 0) {
      element.textContent = message;
      return;
    }
    element.append(message.slice(0, index), replacement, message.slice(index + token.length));
  };

  const closeThemeSelect = (instance, { restoreFocus = false } = {}) => {
    if (!instance?.open) return;
    instance.open = false;
    instance.root.classList.remove('is-open');
    instance.trigger.setAttribute('aria-expanded', 'false');
    window.clearTimeout(instance.closeTimer);
    instance.closeTimer = window.setTimeout(() => {
      if (!instance.open) instance.list.hidden = true;
    }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 200);
    if (openThemeSelect === instance) openThemeSelect = null;
    if (restoreFocus) instance.trigger.focus();
  };

  const enhanceThemeSelect = (select) => {
    if (!select || select.dataset.themeSelectEnhanced === 'true') return select?.themeSelectInstance || null;
    const root = createElement('div', { class: 'theme-select' });
    const trigger = createElement('button', {
      type: 'button', class: 'theme-select__trigger', 'aria-haspopup': 'listbox', 'aria-expanded': 'false'
    });
    const value = createElement('span', { class: 'theme-select__value' });
    const indicator = createElement('span', { class: 'theme-select__indicator', 'aria-hidden': 'true' });
    const listId = `${select.id}-options`;
    const list = createElement('div', { id: listId, class: 'theme-select__list', role: 'listbox', hidden: true });
    trigger.setAttribute('aria-controls', listId);
    const label = select.labels?.[0];
    if (label) {
      if (!label.id) label.id = `${select.id}-label`;
      trigger.setAttribute('aria-labelledby', label.id);
    } else {
      trigger.setAttribute('aria-label', select.getAttribute('aria-label') || select.name || 'Select');
    }
    if (select.getAttribute('aria-required') === 'true') trigger.setAttribute('aria-required', 'true');
    if (select.getAttribute('aria-describedby')) trigger.setAttribute('aria-describedby', select.getAttribute('aria-describedby'));
    trigger.append(value, indicator);
    const instance = { root, trigger, list, select, options: [], open: false, closeTimer: null };

    const sync = () => {
      const selected = select.options[select.selectedIndex] || select.options[0];
      value.textContent = selected?.textContent || '';
      root.classList.toggle('is-placeholder', !select.value);
      root.classList.toggle('is-disabled', select.disabled);
      trigger.disabled = select.disabled;
      trigger.setAttribute('aria-invalid', select.getAttribute('aria-invalid') === 'true' ? 'true' : 'false');
      instance.options.forEach((option) => {
        const active = option.dataset.value === select.value;
        option.setAttribute('aria-selected', active ? 'true' : 'false');
        option.classList.toggle('is-selected', active);
      });
    };
    instance.sync = sync;
    instance.position = () => {
      const rect = trigger.getBoundingClientRect();
      const viewport = window.visualViewport;
      let top = viewport?.offsetTop || 0;
      let bottom = top + (viewport?.height || window.innerHeight);
      // Use the visible content area, not space occupied by the theme navigation.
      document.querySelectorAll('.hdr-wrap, .hdr-content-sticky, .hdr-mobile-bottom').forEach((navigation) => {
        const bounds = navigation.getBoundingClientRect();
        const style = getComputedStyle(navigation);
        if (!bounds.width || !bounds.height || bounds.right <= rect.left || bounds.left >= rect.right
          || bounds.bottom <= top || bounds.top >= bottom || style.display === 'none'
          || style.visibility === 'hidden' || Number(style.opacity) === 0) return;
        if (navigation.matches('.hdr-mobile-bottom')) bottom = Math.min(bottom, bounds.top);
        else top = Math.max(top, bounds.bottom);
      });
      const below = Math.max(0, bottom - rect.bottom - 20);
      const above = Math.max(0, rect.top - top - 20);
      const openAbove = list.scrollHeight > below && above > below;
      root.classList.toggle('is-above', openAbove);
      list.style.maxHeight = `${Math.floor(openAbove ? above : below)}px`;
    };
    const enabledOptions = () => instance.options.filter((option) => !option.disabled);
    const focusOption = (index) => {
      const enabled = enabledOptions();
      if (enabled.length) enabled[Math.max(0, Math.min(index, enabled.length - 1))].focus();
    };
    const open = () => {
      if (select.disabled || instance.open) return;
      if (openThemeSelect && openThemeSelect !== instance) closeThemeSelect(openThemeSelect);
      window.clearTimeout(instance.closeTimer);
      instance.open = true;
      openThemeSelect = instance;
      trigger.setAttribute('aria-expanded', 'true');
      list.hidden = false;
      instance.position();
      void list.offsetHeight;
      root.classList.add('is-open');
      const selectedIndex = enabledOptions().findIndex((option) => option.dataset.value === select.value);
      focusOption(selectedIndex < 0 ? 0 : selectedIndex);
    };
    const choose = (option) => {
      if (!option || option.disabled) return;
      select.value = option.dataset.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      sync();
      closeThemeSelect(instance, { restoreFocus: true });
    };

    const groups = new Map();
    Array.from(select.options).forEach((nativeOption) => {
      if (nativeOption.hidden) return;
      const nativeGroup = nativeOption.parentElement?.tagName === 'OPTGROUP' ? nativeOption.parentElement : null;
      let container = list;
      if (nativeGroup) {
        if (!groups.has(nativeGroup)) {
          const headingId = `${listId}-group-${groups.size}`;
          const group = createElement('div', { class: 'theme-select__group', role: 'group', 'aria-labelledby': headingId });
          group.append(createElement('div', { id: headingId, class: 'theme-select__group-label', role: 'presentation' }, nativeGroup.label));
          groups.set(nativeGroup, group);
          list.append(group);
        }
        container = groups.get(nativeGroup);
      }
      const option = createElement('button', {
        type: 'button', class: 'theme-select__option', role: 'option',
        disabled: nativeOption.disabled || nativeGroup?.disabled
      }, nativeOption.textContent);
      option.dataset.value = nativeOption.value;
      option.addEventListener('click', () => choose(option));
      option.addEventListener('keydown', (event) => {
        const enabled = enabledOptions();
        const current = enabled.indexOf(option);
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          focusOption(current + (event.key === 'ArrowDown' ? 1 : -1));
        } else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          focusOption(event.key === 'Home' ? 0 : enabled.length - 1);
        } else if (event.key === 'Escape') {
          event.preventDefault();
          closeThemeSelect(instance, { restoreFocus: true });
        } else if (event.key === 'Tab') closeThemeSelect(instance);
      });
      instance.options.push(option);
      container.append(option);
    });
    trigger.addEventListener('click', () => instance.open ? closeThemeSelect(instance) : open());
    trigger.addEventListener('keydown', (event) => {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        event.preventDefault();
        open();
      } else if (event.key === 'Escape') closeThemeSelect(instance);
    });
    select.addEventListener('change', sync);
    new MutationObserver(sync).observe(select, { attributes: true, attributeFilter: ['aria-invalid', 'disabled'] });
    select.dataset.themeSelectEnhanced = 'true';
    select.themeSelectInstance = instance;
    select.classList.add('theme-select__native');
    select.parentElement?.classList.add('theme-select-wrap');
    select.insertAdjacentElement('afterend', root);
    root.append(trigger, list);
    sync();
    return instance;
  };

  document.addEventListener('pointerdown', (event) => {
    if (openThemeSelect && !openThemeSelect.root.contains(event.target)) closeThemeSelect(openThemeSelect);
  });
  const repositionThemeSelect = () => openThemeSelect?.position();
  window.addEventListener('resize', repositionThemeSelect, { passive: true });
  window.addEventListener('scroll', repositionThemeSelect, { passive: true, capture: true });
  window.visualViewport?.addEventListener('resize', repositionThemeSelect, { passive: true });

  const enhanceThemeSelects = (root = document) => {
    if (root.matches?.('[data-product-input-fields] select.form-select')) enhanceThemeSelect(root);
    root.querySelectorAll?.('[data-product-input-fields] select.form-select').forEach(enhanceThemeSelect);
  };

  const enhanceProductInputHelp = (help) => {
    if (!help || help.dataset.productInputHelpEnhanced === 'true') return help;
    if (help.dataset.productInputHelpPending === 'true') return help;
    const trigger = help.querySelector('[data-product-input-help-trigger]');
    const panel = help.querySelector('[data-product-input-help-content]');
    if (!trigger || !panel) return help;

    const initialize = () => {
      if (help.dataset.productInputHelpEnhanced === 'true') return;
      if (!help.isConnected) {
        delete help.dataset.productInputHelpPending;
        return;
      }
      if (typeof window.tippy !== 'function') {
        if (document.readyState !== 'complete') window.addEventListener('load', initialize, { once: true });
        return;
      }
      panel.hidden = false;
      help.productInputHelpInstance = window.tippy(trigger, {
        content: panel,
        appendTo: () => document.body,
        interactive: true,
        trigger: 'mouseenter focus click',
        touch: true,
        hideOnClick: true,
        maxWidth: 360,
        theme: document.body.dataset.tooltipColor,
        animation: document.body.dataset.tooltipAnimation,
        duration: [document.body.dataset.tooltipDuration || 300, 0],
        delay: document.body.dataset.tooltipDelay || 0,
        zIndex: 100000,
        onShow: () => trigger.setAttribute('aria-expanded', 'true'),
        onHidden: () => trigger.setAttribute('aria-expanded', 'false')
      });
      help.dataset.productInputHelpEnhanced = 'true';
      delete help.dataset.productInputHelpPending;
    };

    help.dataset.productInputHelpPending = 'true';
    if (typeof window.tippy === 'function') initialize();
    else document.addEventListener('DOMContentLoaded', initialize, { once: true });
    return help;
  };

  const destroyProductInputHelp = (help) => {
    help?.productInputHelpInstance?.destroy();
    if (help) delete help.productInputHelpInstance;
  };

  const enhanceProductInputHelps = (root = document) => {
    if (root.matches?.('[data-product-input-help]')) enhanceProductInputHelp(root);
    root.querySelectorAll?.('[data-product-input-help]').forEach(enhanceProductInputHelp);
  };

  const enhanceUniqueIdInputs = (root = document) => {
    if (root.matches?.('[data-eight-bp-unique-id]')) uniqueIdState.enhance(root);
    root.querySelectorAll?.('[data-eight-bp-unique-id]').forEach((input) => uniqueIdState.enhance(input));
  };

  const destroyProductInputHelps = (root) => {
    if (root.matches?.('[data-product-input-help]')) destroyProductInputHelp(root);
    root.querySelectorAll?.('[data-product-input-help]').forEach(destroyProductInputHelp);
  };

  const enhanceProductInputs = (root = document) => {
    enhanceThemeSelects(root);
    enhanceProductInputHelps(root);
    enhanceUniqueIdInputs(root);
  };

  window.ThemeSelect = { enhance: enhanceThemeSelect, enhanceAll: enhanceThemeSelects };
  window.ProductInputHelp = {
    enhance: enhanceProductInputHelp,
    enhanceAll: enhanceProductInputHelps,
    destroy: destroyProductInputHelp,
    destroyAll: destroyProductInputHelps
  };
  new MutationObserver((records) => {
    records.forEach((record) => {
      record.removedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) destroyProductInputHelps(node);
      });
      record.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) enhanceProductInputs(node);
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  if (!customElements.get('eight-bp-account-input')) {
    customElements.define('eight-bp-account-input', class EightBpAccountInput extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        const config = readConfig(this);
        // A submission link offers "new account" only when staff allowed it for that order.
        this.providers = this.dataset.allowNewAccount === 'false'
          ? config.providers.filter((provider) => provider.key !== 'new_account')
          : config.providers;
        this.providers.sort((a, b) => Number(a.key === 'new_account') - Number(b.key === 'new_account'));
        this.accountLoginUrl = config.accountLoginUrl;
        this.translations = config.translations;
        this.accounts = [];
        this.authenticated = null;
        this.mode = this.dataset.existingReference ? 'existing' : 'new';
        this.dirty = false;
        this.correctionFields = (this.dataset.correctionFields || '').split(',').filter(Boolean);
        this.renderShell();
        if (!this.isCorrection || !this.renderCorrection()) this.showFullForm();
      }

      get isCorrection() {
        return this.correctionFields.length > 0;
      }

      t(key, replacements = {}) {
        const value = this.translations[key];
        if (typeof value !== 'string') return '';
        return Object.entries(replacements).reduce(
          (message, [name, replacement]) => message.replaceAll(`__${name.toUpperCase()}__`, String(replacement)),
          value
        );
      }

      provider(key) {
        return this.providers.find((provider) => provider.key === key);
      }

      providerLabel(key) {
        const provider = this.provider(key);
        return this.t(provider?.labelKey) || key;
      }

      providerFields(provider) {
        return Array.isArray(provider?.fields)
          ? [...provider.fields].sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
          : [];
      }

      async requestJson(path, options = {}) {
        const response = await fetch(`${API_ROOT}${path}`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json', ...(options.body ? { 'Content-Type': 'application/json' } : {}) },
          ...options
        });
        const body = response.status === 204 ? null : await response.json().catch(() => null);
        if (!response.ok) {
          const error = new Error(this.t(body?.code) || body?.error || this.t('request_failed'));
          error.code = body?.code || 'request_failed';
          throw error;
        }
        return body;
      }

      async getCartContext() {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const response = await fetch(`${root}cart.js?_=${Date.now()}`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
        });
        const cart = response.ok ? await response.json().catch(() => null) : null;
        const token = String(cart?.token || '').trim();
        if (!token || token.length > 512) throw new Error(this.t('cart_context_error'));
        return token;
      }

      renderShell() {
        this.classList.add('eight-bp-account-input');
        this.querySelector('[data-8bp-account-config]')?.remove();
        this.status = createElement('div', {
          class: 'eight-bp-account-input__status',
          role: 'status',
          'aria-live': 'polite',
          hidden: true
        });
        this.status.append(createElement('div', { 'data-load': 'loading', 'aria-hidden': 'true' }));
        this.error = createErrorBox(this);
        this.controls = createElement('div', { class: 'eight-bp-account-input__controls' });
        this.controls.hidden = true;
        this.append(this.status, this.error, this.controls);
      }

      showFullForm() {
        this.correctionFields = [];
        this.controls.hidden = true;
        this.setStatus(this.t('loading'));
        this.loadAccounts();
      }

      renderCorrection() {
        const provider = this.provider(this.dataset.correctionType);
        const fields = this.providerFields(provider).filter((field) => this.correctionFields.includes(field.key));
        // Any requested detail this provider cannot show means the whole account is entered instead.
        if (!provider?.directSubmission || fields.length !== this.correctionFields.length) return false;
        this.controls.replaceChildren();
        this.groupFields = createElement('div', { class: 'eight-bp-account-input__provider-fields' });
        this.controls.append(this.groupFields);
        this.credentialInputs = {};
        this.backupInputs = [];
        this.uniqueIdInput = null;
        // Every detail staff asked for must be provided, including otherwise optional ones.
        fields.forEach((field) => this.renderCredentialField({ ...field, required: true }));
        this.enhanceUniqueIdInput();
        this.controls.hidden = false;
        return true;
      }

      resetDraft() {
        if (this.referenceField) this.referenceField.value = this.dataset.existingReference || '';
        this.mode = this.dataset.existingReference ? 'existing' : 'new';
        this.editingAccount = null;
        this.selectedType = '';
        this.dirty = false;
        this.showError('');
        this.renderControls();
      }

      async loadAccounts() {
        try {
          const result = await this.requestJson('/accounts');
          this.accounts = Array.isArray(result?.accounts) ? result.accounts : [];
          this.authenticated = Boolean(result?.authenticated);
        } catch (error) {
          this.accounts = [];
          this.authenticated = null;
        } finally {
          this.controls.hidden = false;
          this.setStatus('');
        }
        this.renderControls();
      }

      setStatus(message) {
        if (!this.status) return;
        const label = message || '';
        this.status.hidden = !label;
        if (label) this.status.setAttribute('aria-label', label);
        else this.status.removeAttribute('aria-label');
      }

      showError(message) {
        setErrorBox(this.error, message);
      }

      requiredMark() {
        return createElement('span', { class: 'product-input-field__required', 'aria-hidden': 'true' }, '*');
      }

      helpDisclosure(label, help, describedInput) {
        if (!help?.title || !Array.isArray(help.steps) || !help.steps.length) return null;
        const helpRoot = createElement('span', {
          class: 'product-input-field__help',
          'data-product-input-help': true
        });
        const panelId = `${describedInput.id}-help`;
        const trigger = createElement('button', {
          type: 'button',
          class: 'product-input-field__help-trigger',
          'aria-label': this.t('help_label', { field: label }),
          'aria-controls': panelId,
          'aria-expanded': 'false',
          'data-product-input-help-trigger': true
        });
        const iconTemplate = this.querySelector('[data-product-input-help-icon]');
        const icon = iconTemplate?.content?.firstElementChild?.cloneNode(true);
        if (icon) {
          icon.setAttribute('aria-hidden', 'true');
          icon.removeAttribute('width');
          icon.removeAttribute('height');
          trigger.append(icon);
        }
        const panel = createElement('div', {
          id: panelId,
          class: 'product-input-field__help-panel',
          'data-product-input-help-content': true,
          hidden: true
        });
        panel.append(createElement('strong', { class: 'product-input-field__help-title' }, help.title));
        const list = createElement('ol', { class: 'product-input-field__help-steps' });
        help.steps.filter(Boolean).forEach((step) => list.append(createElement('li', {}, step)));
        panel.append(list);
        helpRoot.append(trigger, panel);
        describedInput.setAttribute('aria-describedby', panelId);
        return helpRoot;
      }

      passwordControl(input, label) {
        const control = createElement('div', { class: 'form-control-wrap product-input-field__password-wrap' });
        const button = createElement('button', {
          type: 'button', class: 'product-input-field__password-toggle',
          'aria-label': this.t('show_password', { field: label }), 'aria-pressed': 'false'
        });
        const icon = this.querySelector('[data-product-input-password-icon]')?.content?.firstElementChild?.cloneNode(true);
        if (icon) { icon.setAttribute('aria-hidden', 'true'); icon.removeAttribute('width'); icon.removeAttribute('height'); button.append(icon); }
        button.addEventListener('click', () => {
          const visible = input.type === 'password';
          input.type = visible ? 'text' : 'password';
          button.classList.toggle('is-visible', visible);
          button.setAttribute('aria-pressed', String(visible));
          button.setAttribute('aria-label', this.t(visible ? 'hide_password' : 'show_password', { field: label }));
          input.focus({ preventScroll: true });
        });
        control.append(input, button);
        return control;
      }

      field(label, input, { help = null, required = true } = {}) {
        const wrapper = createElement('div', {
          class: 'eight-bp-account-input__field form-group',
          'data-product-option-field-wrapper': true
        });
        const labelRow = createElement('div', { class: 'product-input-field__label-row' });
        const labelElement = createElement('label', { class: 'form-label', for: input.id });
        labelElement.append(createElement('span', { 'data-8bp-field-label': true }, label));
        if (required) labelElement.append(this.requiredMark());
        labelRow.append(labelElement);
        const disclosure = this.helpDisclosure(label, help, input);
        if (disclosure) labelRow.append(disclosure);
        const controlWrap = input.type === 'password'
          ? this.passwordControl(input, label)
          : createElement('div', {
            class: `form-control-wrap${input.tagName === 'SELECT' ? ' form-control-wrap--select' : ''}`
          });
        if (input.type !== 'password') controlWrap.append(input);
        wrapper.append(labelRow, controlWrap);
        if (input.tagName === 'SELECT') enhanceThemeSelect(input);
        return wrapper;
      }

      finishSecureSubmission(result) {
        const reference = String(result?.reference || '').trim();
        if (!reference) throw new Error(this.t('request_failed'));
        [...Object.values(this.credentialInputs || {}), ...(this.backupInputs || [])]
          .filter(Boolean).forEach((input) => { input.value = ''; });
        this.referenceField.value = reference;
        try { localStorage.setItem(`bbirdg:8bp-account-context:${reference}`, this._lastCartContext || ''); } catch (_) {}
        this.dataset.existingReference = reference;
        this.dataset.existingMode = ['new_account', 'provide_later'].includes(result.summary?.account_type) ? result.summary.account_type : '';
        this.querySelector('[data-8bp-account-mode]').value = this.dataset.existingMode;
        this.mode = 'existing';
        this.editingAccount = null;
        this.selectedType = '';
        this.dirty = false;
        this.setStatus('');
        this.renderControls();
      }

      markDirty() {
        this.dirty = true;
        this.referenceField.value = '';
        this.querySelectorAll('[aria-invalid="true"]').forEach((field) => field.removeAttribute('aria-invalid'));
        this.showError('');
      }

      renderControls() {
        if (!this.controls) return;
        this.controls.replaceChildren();
        if (this.dataset.existingReference && this.mode === 'existing') {
          const existing = createElement('div', { class: 'eight-bp-account-input__saved-card' });
          existing.append(
            createElement('strong', {}, this.t(this.dataset.existingMode === 'provide_later' ? 'provide_later_selected' : this.dataset.existingMode === 'new_account' ? 'new_account_requested' : 'details_selected')),
            this.dataset.existingMode === 'provide_later' ? this.provideLaterNotice() : createElement('p', {}, this.t(this.dataset.existingMode === 'new_account' ? 'new_account_notice' : 'reference_kept')),
            this.actionButton(this.t('use_different'), () => {
              this.mode = 'new';
              this.markDirty();
              this.renderControls();
            })
          );
          this.controls.append(existing);
          return;
        }

        if (this.accounts.length) {
          const sourceId = `${this.id}-source`;
          const source = createElement('select', { id: sourceId, class: 'form-select form-control form-control--rounded' });
          source.append(createElement('option', { value: 'new' }, this.t('enter_new')));
          this.accounts.forEach((account) => {
            const suffix = account.is_default ? ` — ${this.t('default_suffix')}` : '';
            const summary = account.masked_email || account.masked_unique_id || '';
            source.append(createElement('option', { value: account.id }, `${this.providerLabel(account.account_type)} · ${summary}${suffix}`));
          });
          const reusable = this.accounts.filter((account) => (account.health_status || 'ready') === 'ready');
          const preferred = reusable.find((account) => account.is_default) || reusable[0];
          if (this.mode !== 'new' && this.accounts.some((account) => account.id === this.mode)) source.value = this.mode;
          else if (!this.dirty && preferred) {
            this.mode = preferred.id;
            source.value = preferred.id;
          }
          source.addEventListener('change', () => {
            this.mode = source.value;
            this.markDirty();
            this.renderControls();
          });
          this.controls.append(this.field(this.t('choose_account'), source));
        }

        const selectedAccount = this.accounts.find((account) => account.id === this.mode);
        if (selectedAccount) {
          this.renderSavedAccount(selectedAccount);
          return;
        }
        this.mode = 'new';
        this.renderNewAccount();
      }

      renderSavedAccount(account) {
        const card = createElement('div', { class: 'eight-bp-account-input__saved-card' });
        const actions = createElement('div', { class: 'eight-bp-account-input__saved-actions' });
        actions.append(
          this.actionButton(this.t('edit'), () => this.startEditingAccount(account)),
          this.actionButton(this.t('delete'), () => this.deleteSavedAccount(account))
        );
        card.append(
          createElement('strong', {}, this.t('account_heading', { provider: this.providerLabel(account.account_type) })),
          createElement('p', {}, [account.masked_email, account.masked_unique_id].filter(Boolean).join(' · ')),
          createElement(
            'p',
            { class: account.health_status === 'ready' ? '' : 'eight-bp-account-input__health-warning' },
            account.health_reason === 'facebook_gmail_not_supported'
              ? this.t('facebook_gmail_update_required')
              : this.t(account.health_status === 'ready' ? 'ready_to_use' : 'update_required')
          ),
          actions
        );
        this.controls.append(card);
      }

      async startEditingAccount(account) {
        if (!account?.id || this.hasAttribute('aria-busy')) return;
        this.setStatus(this.t('loading'));
        this.showError('');
        this.setAttribute('aria-busy', 'true');
        try {
          const result = await this.requestJson(`/accounts/${encodeURIComponent(account.id)}`);
          const editable = result?.account;
          if (!editable || editable.id !== account.id || editable.account_type !== account.account_type) {
            throw new Error(this.t('request_failed'));
          }
          this.mode = 'edit';
          this.editingAccount = { ...account, ...editable };
          this.selectedType = account.account_type;
          this.markDirty();
          this.renderNewAccount(account.account_type);
        } catch (error) {
          this.showError(error.message || this.t('request_failed'));
        } finally {
          this.setStatus('');
          this.removeAttribute('aria-busy');
        }
      }

      async deleteSavedAccount(account) {
        if (!account?.id || !window.confirm(this.t('delete_confirm'))) return;
        this.setStatus(this.t('loading_account'));
        this.showError('');
        try {
          await this.requestJson(`/accounts/${encodeURIComponent(account.id)}`, { method: 'DELETE' });
          this.accounts = this.accounts.filter((candidate) => candidate.id !== account.id);
          this.mode = 'new';
          this.editingAccount = null;
          this.dirty = false;
          this.removeAttribute('data-existing-reference');
          this.referenceField.value = '';
          this.setStatus('');
        } catch (error) {
          this.showError(error.message);
        }
        this.renderControls();
      }

      actionButton(label, handler) {
        const button = createElement('button', { type: 'button', class: 'btn-link btn-link--sm' }, label);
        button.addEventListener('click', handler);
        return button;
      }

      hasCredentialDraft() {
        return [...Object.values(this.credentialInputs || {}), ...(this.backupInputs || [])]
          .some((input) => String(input?.value || '').trim());
      }

      resetAccountSelection() {
        if (this.hasCredentialDraft() && !window.confirm(this.t('discard_account_confirm'))) return;
        const editedAccountId = this.editingAccount?.id || '';
        this.editingAccount = null;
        this.selectedType = '';
        this.showError('');
        if (editedAccountId && this.accounts.some((account) => account.id === editedAccountId)) {
          this.mode = editedAccountId;
          this.dirty = false;
        } else if (this.dataset.existingReference) {
          this.mode = 'existing';
          this.referenceField.value = this.dataset.existingReference;
          this.dirty = false;
        } else {
          this.mode = 'new';
          this.markDirty();
        }
        this.renderControls();
      }

      renderNewAccount(lockedType = '') {
        this.controls.replaceChildren();
        if (this.accounts.length && !lockedType) {
          this.controls.append(this.actionButton(this.t('use_saved'), () => {
            this.mode = (this.accounts.find((account) => account.is_default) || this.accounts[0]).id;
            this.renderControls();
          }));
        }

        const typeId = `${this.id}-account-type`;
        this.typeSelect = createElement('select', {
          id: typeId,
          class: 'form-select form-control form-control--rounded',
          'aria-required': 'true',
          disabled: Boolean(lockedType)
        });
        this.typeSelect.append(createElement('option', { value: '', disabled: true, hidden: true }, this.t('select_account_type')));
        const existingGroup = createElement('optgroup', { label: this.t('existing_account_group') });
        const newGroup = createElement('optgroup', { label: this.t('new_account_group') });
        const laterGroup = createElement('optgroup', { label: this.t('provide_later_group') });
        this.providers.forEach((provider) => {
          const group = provider.key === 'provide_later' ? laterGroup : provider.key === 'new_account' ? newGroup : existingGroup;
          group.append(createElement('option', { value: provider.key }, this.t(provider.labelKey) || provider.key));
        });
        if (laterGroup.children.length) this.typeSelect.append(laterGroup);
        if (existingGroup.children.length) this.typeSelect.append(existingGroup);
        if (newGroup.children.length) this.typeSelect.append(newGroup);
        this.typeSelect.value = lockedType || this.selectedType || '';
        this.typeSelect.classList.toggle('is-placeholder', !this.typeSelect.value);
        this.typeSelect.addEventListener('change', () => {
          this.selectedType = this.typeSelect.value;
          this.typeSelect.classList.toggle('is-placeholder', !this.typeSelect.value);
          if (this.accountResetButton) this.accountResetButton.hidden = !this.typeSelect.value;
          this.markDirty();
          this.renderCredentialFields();
        });
        const typeField = this.field(this.t('account_type'), this.typeSelect);
        this.accountResetButton = createClearButton(
          [this.t('clear'), this.t('account_type')].filter(Boolean).join(' '),
          () => this.resetAccountSelection(),
          this.querySelector('[data-product-input-clear-icon]')
        );
        this.accountResetButton.hidden = !this.typeSelect.value;
        this.accountResetButton.classList.add('eight-bp-account-input__reset');
        typeField.querySelector('.product-input-field__label-row')?.append(this.accountResetButton);
        this.controls.append(typeField);
        this.dynamicFields = createElement('div', { class: 'eight-bp-account-input__dynamic' });
        this.controls.append(this.dynamicFields);
        this.renderCredentialFields();
      }

      provideLaterNotice() {
        return supportMessage(
          createElement('p', { class: 'eight-bp-account-input__notice' }),
          this.t('provide_later_notice'),
          { chat: this.t('live_chat_sign_in'), telegram: this.t('telegram_order_number') }
        );
      }

      credentialInput(field, value = '') {
        const key = field.key;
        const label = this.t(field.labelKey) || field.labelKey || key;
        const input = this.credentialInputElement(field, value);
        input.dataset.validationLabel = label;
        this.groupFields.append(this.field(label, input, { help: this.translations[field.tooltipKey], required: Boolean(field.required) }));
        return input;
      }

      renderFacebookLogin(field, value = '') {
        const methodName = `${this.id}-facebook-login-method`;
        const currentMethod = /^\+[\d\s().-]+$/.test(String(value || '').trim()) ? 'phone' : 'email';
        const methodGroup = createElement('fieldset', {
          class: 'eight-bp-account-input__login-method custom-form form-group'
        });
        methodGroup.append(createElement('legend', { class: 'form-label' }, this.t('facebook_login_method')));
        const choices = createElement('div', { class: 'eight-bp-account-input__login-method-options' });
        const input = this.credentialInputElement(field, value);
        const inputWrapper = this.field(this.t('facebook_email'), input, { required: Boolean(field.required) });

        const applyMethod = (method, { clear = false, focus = false } = {}) => {
          const phone = method === 'phone';
          const label = this.t(phone ? 'facebook_phone' : 'facebook_email');
          input.type = phone ? 'tel' : 'email';
          input.inputMode = phone ? 'tel' : 'email';
          input.autocomplete = phone ? 'tel' : 'email';
          input.pattern = phone ? '^\\+[1-9][0-9]{6,14}$' : '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$';
          input.placeholder = this.t(phone ? 'facebook_phone_placeholder' : 'facebook_email_placeholder');
          input.dataset.validationMode = phone ? 'facebook_phone' : 'facebook_email';
          input.dataset.validationLabel = label;
          const labelText = inputWrapper.querySelector('[data-8bp-field-label]');
          if (labelText) labelText.textContent = label;
          if (clear) input.value = '';
          if (focus) {
            try { input.focus({ preventScroll: true }); }
            catch (error) { input.focus(); }
          }
        };

        ['email', 'phone'].forEach((method) => {
          const radio = createElement('input', {
            id: `${methodName}-${method}`,
            name: methodName,
            type: 'radio',
            value: method,
            checked: method === currentMethod
          });
          const label = createElement('label', { for: radio.id }, this.t(method === 'email' ? 'login_email' : 'login_phone'));
          radio.addEventListener('change', () => {
            if (!radio.checked) return;
            applyMethod(method, { clear: true, focus: true });
            this.markDirty();
          });
          choices.append(radio, label);
        });
        methodGroup.append(choices);
        applyMethod(currentMethod);
        this.groupFields.append(methodGroup, inputWrapper);
        return input;
      }

      secretEditControl(key, label, createEditor, help = null) {
        const wrapper = createElement('div', { class: 'eight-bp-account-input__field eight-bp-account-input__secret-edit form-group' });
        const labelRow = createElement('div', { class: 'product-input-field__label-row' });
        const labelElement = createElement('span', { class: 'form-label' }, label);
        labelRow.append(labelElement);
        const describedInput = {
          id: `${this.id}-${key}-saved`,
          setAttribute: (name, value) => wrapper.setAttribute(name, value)
        };
        const disclosure = this.helpDisclosure(label, help, describedInput);
        if (disclosure) labelRow.append(disclosure);
        const editButton = this.actionButton(this.t('edit'), () => {
          const editor = createEditor();
          if (!editor?.element) return;
          wrapper.replaceWith(editor.element);
          try { editor.focusTarget?.focus({ preventScroll: true }); }
          catch (error) { editor.focusTarget?.focus(); }
        });
        editButton.classList.add('eight-bp-account-input__secret-edit-action');
        labelRow.append(editButton);
        wrapper.append(labelRow);
        this.groupFields.append(wrapper);
      }

      createBackupCodes(field, requestedIndexes = null) {
        const count = Math.max(0, Math.min(10, Number(field.itemCount) || 0));
        if (!count) return { element: null, inputs: [] };
        const indexes = Array.isArray(requestedIndexes)
          ? [...new Set(requestedIndexes)].filter((index) => Number.isInteger(index) && index >= 0 && index < count).sort()
          : Array.from({ length: count }, (_, index) => index);
        if (!indexes.length) return { element: null, inputs: [] };
        const fieldset = createElement('fieldset', { class: 'eight-bp-account-input__codes' });
        const label = this.t(field.labelKey);
        const labelRow = createElement('div', { class: 'product-input-field__label-row eight-bp-account-input__codes-heading' });
        const legend = createElement('legend', { class: 'form-label' }, label);
        legend.append(this.requiredMark());
        labelRow.append(legend);
        const help = this.translations[field.tooltipKey];
        const describedInput = {
          id: `${this.id}-backup-codes`,
          setAttribute: (name, value) => fieldset.setAttribute(name, value)
        };
        const disclosure = this.helpDisclosure(label, help, describedInput);
        if (disclosure) labelRow.append(disclosure);
        const inputs = [];
        const controls = indexes.map((index) => {
          const number = index + 1;
          const inputLabel = this.t('backup_code', { number });
          const input = createElement('input', {
            id: `${this.id}-backup-code-${number}`,
            class: 'form-control form-control--rounded',
            type: 'password',
            dir: 'ltr',
            'aria-required': 'true',
            autocomplete: 'off',
            maxlength: Math.max(1, Math.min(1000, Number(field.maxLength) || 120)),
            placeholder: inputLabel,
            'aria-label': `${label}: ${inputLabel}`
          });
          input.dataset.backupCodeIndex = String(index);
          input.dataset.validationLabel = inputLabel;
          input.addEventListener('input', () => this.markDirty());
          inputs.push(input);
          return this.passwordControl(input, inputLabel);
        });
        const grid = createElement('div', { class: 'eight-bp-account-input__codes-grid' });
        grid.append(...controls);
        fieldset.append(labelRow, grid);
        return { element: fieldset, inputs };
      }

      renderCredentialFields() {
        if (!this.dynamicFields || !this.typeSelect) return;
        this.dynamicFields.replaceChildren();
        this.credentialInputs = {};
        this.backupInputs = [];
        this.saveCheckbox = null;
        this.defaultCheckbox = null;
        const provider = this.provider(this.typeSelect.value);
        if (!provider) return;
        const group = createElement('section', {
          class: 'eight-bp-account-input__provider-group',
          'aria-label': this.providerLabel(provider.key)
        });
        this.groupFields = createElement('div', { class: 'eight-bp-account-input__provider-fields' });
        group.append(this.groupFields);
        this.dynamicFields.append(group);

        if (provider.key === 'provide_later') {
          this.groupFields.append(this.provideLaterNotice());
          return;
        }
        if (!provider.directSubmission || provider.key === 'new_account') {
          this.groupFields.append(createElement('div', { class: 'eight-bp-account-input__notice', role: 'note' }, this.t(provider.noticeKey)));
          return;
        }
        this.credentialInputs = {};
        this.emailInput = null;
        this.passwordInput = null;
        this.uniqueIdInput = null;
        this.backupInputs = [];
        this.providerFields(provider).forEach((field) => this.renderCredentialField(field));
        this.enhanceUniqueIdInput();
        this.renderSaveControls();
      }

      renderCredentialField(field) {
        const editing = Boolean(this.editingAccount);
        const label = this.t(field.labelKey) || field.labelKey || field.key;
        if (field.type === 'backup_codes') {
          if (editing) {
            const requiredIndexes = this.editingAccount.required_backup_code_indexes || [];
            if (requiredIndexes.length) {
              const editor = this.createBackupCodes(field, requiredIndexes);
              this.backupInputs = editor.inputs;
              if (editor.element) this.groupFields.append(editor.element);
            } else {
              this.secretEditControl(
                'backup-codes',
                label,
                () => {
                  const editor = this.createBackupCodes(field);
                  this.backupInputs = editor.inputs;
                  return { element: editor.element, focusTarget: editor.inputs[0] };
                },
                this.translations[field.tooltipKey]
              );
            }
          } else {
            const editor = this.createBackupCodes(field);
            this.backupInputs = editor.inputs;
            if (editor.element) this.groupFields.append(editor.element);
          }
          return;
        }
        if (editing && field.secret) {
          this.secretEditControl(field.key, label, () => {
            const input = this.credentialInputElement(field);
            this.credentialInputs[field.key] = input;
            if (field.key === 'password') this.passwordInput = input;
            return { element: this.field(label, input, { help: this.translations[field.tooltipKey], required: Boolean(field.required) }), focusTarget: input };
          }, this.translations[field.tooltipKey]);
          return;
        }
        const existingValue = editing ? String(this.editingAccount[field.key] || (field.key === 'login_identifier' ? this.editingAccount.email : '') || '') : '';
        const input = field.validation === 'email_or_international_phone'
          ? this.renderFacebookLogin(field, existingValue)
          : this.credentialInput(field, existingValue);
        this.credentialInputs[field.key] = input;
        if (field.key === 'login_identifier') this.emailInput = input;
        if (field.key === 'password') this.passwordInput = input;
        if (field.key === 'unique_id') this.uniqueIdInput = input;
      }

      enhanceUniqueIdInput() {
        if (!this.uniqueIdInput) return;
        uniqueIdState.enhance(this.uniqueIdInput, {
          source: this.uniqueIdInput.id,
          label: this.t('unique_id'),
          clearLabel: this.t('clear'),
          closeLabel: this.t('close'),
          recentLabel: this.t('recent_unique_id'),
          iconTemplate: this.querySelector('[data-product-input-clear-icon]')
        });
      }

      credentialInputElement(field, value = '') {
        const type = ['email', 'tel', 'url', 'password'].includes(field.type) ? field.type : 'text';
        const input = createElement('input', {
          id: `${this.id}-${field.key}`,
          class: 'form-control form-control--rounded',
          type,
          // Codes, emails, phone numbers and passwords read left to right in every language;
          // in a right-to-left field "9837 1889" is drawn as "1889 9837". The Unique ID is
          // digits only and keeps the page direction for its clear button.
          dir: field.key === 'unique_id' ? null : 'ltr',
          'aria-required': field.required ? 'true' : 'false',
          required: Boolean(field.required),
          autocomplete: field.autocomplete || 'off',
          placeholder: this.t(field.placeholderKey),
          maxlength: Math.max(1, Math.min(1000, Number(field.maxLength) || 254)),
          value
        });
        if (field.validation === 'email') input.type = 'email';
        if (field.validation === 'email_or_international_phone') input.pattern = '(^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$)|(^\\+[1-9][0-9]{6,14}$)';
        if (field.validation === 'facebook_profile_url') input.pattern = '^https?://(www\\.|m\\.|web\\.)?facebook\\.com/.+';
        if (field.validation === 'digits') {
          input.inputMode = 'numeric';
          input.pattern = '[0-9]+';
        }
        input.dataset.validationMode = field.validation || '';
        input.dataset.credentialKey = field.key;
        input.addEventListener('input', () => this.markDirty());
        return input;
      }

      renderSaveControls() {
        const wrapper = createElement('div', { class: 'eight-bp-account-input__save custom-form' });
        this.saveCheckbox = null;
        this.defaultCheckbox = null;
        if (this.authenticated) {
          const defaultId = `${this.id}-default`;
          if (this.editingAccount) {
            if (!this.editingAccount.is_default) {
              this.defaultCheckbox = createElement('input', { id: defaultId, type: 'checkbox' });
              wrapper.append(
                this.defaultCheckbox,
                createElement('label', { for: defaultId }, this.t('make_default'))
              );
            }
          } else {
            const saveId = `${this.id}-save`;
            this.saveCheckbox = createElement('input', { id: saveId, type: 'checkbox' });
            wrapper.append(
              this.saveCheckbox,
              createElement('label', { for: saveId }, this.t('save_future'))
            );
            if (this.accounts.length) {
              this.defaultCheckbox = createElement('input', { id: defaultId, type: 'checkbox', disabled: true });
              const defaultLabel = createElement('label', { for: defaultId, hidden: true }, this.t('make_default'));
              this.defaultCheckbox.hidden = true;
              wrapper.append(this.defaultCheckbox, defaultLabel);
              this.saveCheckbox.addEventListener('change', () => {
                const visible = this.saveCheckbox.checked;
                this.defaultCheckbox.disabled = !visible;
                this.defaultCheckbox.hidden = !visible;
                defaultLabel.hidden = !visible;
                if (!visible) this.defaultCheckbox.checked = false;
              });
            }
          }
        } else if (this.authenticated === false) {
          const signIn = createElement(
            this.accountLoginUrl ? 'a' : 'span',
            this.accountLoginUrl ? { href: this.accountLoginUrl, class: 'link underlined-link' } : {},
            this.t('sign_in')
          );
          const note = createElement('p', { class: 'eight-bp-account-input__signin-note' });
          appendTokenizedText(note, this.t('sign_in_to_save'), '__SIGN_IN__', signIn);
          this.groupFields.append(note);
        }
        if (wrapper.childElementCount) this.groupFields.append(wrapper);
      }

      validationMessage(field, missing) {
        const label = field?.dataset?.validationLabel || field?.getAttribute?.('aria-label') || this.t('account_type');
        if (missing) return this.t('field_required', { field: label }) || this.t('complete_required');
        if (field?.dataset?.validationMode === 'facebook_phone') return this.t('invalid_facebook_phone');
        if (field?.dataset?.validationMode === 'facebook_profile_url') return this.t('invalid_facebook_profile_url');
        if (field?.dataset?.validationMode === 'digits') return this.t('invalid_unique_id');
        return this.t('field_invalid', { field: label }) || this.t('complete_required');
      }

      invalidateField(field, message, code = 'invalid_option') {
        if (!field) return;
        const form = this.closest('form');
        if (form && window.ProductOptions?.report) {
          window.ProductOptions.report(form, {
            valid: false,
            code,
            field,
            label: field.dataset?.validationLabel || this.t('account_type'),
            message,
            config: { productTitle: '' }
          });
          return;
        }
        field.setAttribute('aria-invalid', 'true');
        const focusTarget = field.themeSelectInstance?.trigger || field;
        try { focusTarget.focus({ preventScroll: true }); }
        catch (error) { focusTarget.focus(); }
      }

      collectCredentials() {
        const kind = this.typeSelect?.value || '';
        [this.typeSelect, ...Object.values(this.credentialInputs || {}), ...(this.backupInputs || [])]
          .filter(Boolean).forEach((input) => input.removeAttribute('aria-invalid'));
        if (!kind) {
          const message = this.t('select_type_error');
          this.invalidateField(this.typeSelect, message, 'required_options');
          throw new Error(message);
        }
        const provider = this.provider(kind);
        if (!provider?.directSubmission) throw new Error(this.t(provider?.noticeKey) || this.t('provider_unavailable'));
        if (['new_account', 'provide_later'].includes(kind)) {
          const variantInput = this.referenceField.form?.querySelector('[name="id"]');
          return {account_type: kind, product_id: this.dataset.productId, variant_id: variantInput?.value || this.dataset.variantId};
        }
        return { account_type: kind, credentials: this.validatedCredentials(kind) };
      }

      collectCorrection() {
        return this.validatedCredentials(this.dataset.correctionType);
      }

      validatedCredentials(kind) {
        const inputs = [...Object.values(this.credentialInputs || {}), ...(this.backupInputs || [])].filter(Boolean);
        const missing = inputs.find((input) => (
          input.getAttribute('aria-required') === 'true' && !String(input.value || '').trim()
        ));
        if (missing) {
          const message = this.validationMessage(missing, true);
          this.invalidateField(missing, message, 'required_options');
          throw new Error(message);
        }
        const invalid = inputs.find((input) => (
          String(input.value || '').trim() && !input.checkValidity()
        ));
        if (invalid) {
          const message = this.validationMessage(invalid, false);
          this.invalidateField(invalid, message);
          throw new Error(message);
        }
        const loginIdentifier = this.credentialInputs?.login_identifier;
        if (
          kind === 'facebook'
          && loginIdentifier
          && isFacebookGmailAddress(loginIdentifier.value)
        ) {
          const message = this.t('facebook_gmail_not_supported');
          this.invalidateField(loginIdentifier, message, 'facebook_gmail_not_supported');
          throw new Error(message);
        }
        return {
          ...Object.fromEntries(Object.entries(this.credentialInputs || {}).map(([key, input]) => [key, input.value])),
          ...(this.backupInputs?.length
            ? this.editingAccount
              ? { backup_code_updates: Object.fromEntries(this.backupInputs.map((input) => [input.dataset.backupCodeIndex, input.value])) }
              : { backup_codes: this.backupInputs.map((input) => input.value) }
            : {})
        };
      }

      async prepareForSubmit() {
        if (this.dataset.existingReference && this.mode === 'existing' && !this.dirty) {
          this.referenceField.value = this.dataset.existingReference;
          return this.referenceField.value;
        }
        this.showError('');
        let profileId = this.accounts.some((account) => account.id === this.mode) ? this.mode : '';
        const payload = profileId ? null : this.collectCredentials();
        this.setStatus(this.t('loading_account'));
        this.setAttribute('aria-busy', 'true');
        try {
          const cartContext = await this.getCartContext();
          this._lastCartContext = cartContext;
          if (!profileId) {
            if (this.authenticated && this.editingAccount) {
              const makeDefault = Boolean(this.editingAccount.is_default || this.defaultCheckbox?.checked);
              const updated = await this.requestJson(`/accounts/${encodeURIComponent(this.editingAccount.id)}`, {
                method: 'PATCH', body: JSON.stringify({ ...payload, make_default: makeDefault })
              });
              profileId = updated.account.id;
            } else if (this.authenticated && this.saveCheckbox?.checked) {
              const makeDefault = this.accounts.length === 0 || Boolean(this.defaultCheckbox?.checked);
              const saved = await this.requestJson('/accounts', {
                method: 'POST', body: JSON.stringify({ ...payload, make_default: makeDefault })
              });
              profileId = saved.account.id;
            } else {
              const result = await this.requestJson('/account-uses', {
                method: 'POST', body: JSON.stringify({ ...payload, cart_context: cartContext })
              });
              this.finishSecureSubmission(result);
              return result.reference;
            }
          }
          const result = await this.requestJson('/account-uses', {
            method: 'POST', body: JSON.stringify({ profile_id: profileId, cart_context: cartContext })
          });
          this.finishSecureSubmission(result);
          return result.reference;
        } catch (error) {
          this.referenceField.value = '';
          throw error;
        } finally {
          this.removeAttribute('aria-busy');
        }
      }

      get referenceField() {
        return this.querySelector('[data-8bp-account-reference]');
      }
    });
  }

  if (!customElements.get('eight-bp-account-correction')) {
    customElements.define('eight-bp-account-correction', class EightBpAccountCorrection extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        let config = {};
        try { config = decodeTranslationTree(JSON.parse(this.querySelector('[data-correction-config]')?.textContent || '{}')); }
        catch (_) {}
        this.accountTemplate = this.querySelector('[data-account-form]');
        const { account, correction } = decodeExtensionCatalog(config.extensionCatalog);
        this.translations = { ...(config.translations || {}), ...account, ...correction };
        this.token = decodeURIComponent(location.hash.slice(1));
        if (this.token) history.replaceState(null, '', `${location.pathname}${location.search}`);
        this.renderLoading();
        if (this.token) this.load();
        else this.renderState('invalid');
      }

      t(key, replacements = {}) {
        const value = this.translations[key] || '';
        return Object.entries(replacements).reduce((message, [name, replacement]) => message.replaceAll(`__${name.toUpperCase()}__`, String(replacement)), value);
      }

      async request(path, payload) {
        const response = await fetch(`${API_ROOT}${path}`, { method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
        const body = await response.json().catch(() => ({}));
        if (!response.ok) { const error = new Error(this.t(body.code) || body.error || this.t('request_failed')); error.code = body.code; throw error; }
        return body;
      }

      renderLoading() {
        const loading = createElement('div', { class: 'eight-bp-correction__loading', role: 'status' });
        loading.append(createElement('div', { 'data-load': 'loading', 'aria-hidden': 'true' }), createElement('span', { class: 'visually-hidden' }, this.t('loading')));
        this.replaceChildren(loading);
      }

      renderState(key, body = null) {
        const box = createElement('div', { class: 'eight-bp-correction__state', role: 'status' });
        box.append(createElement('h1', { class: 'h3' }, this.t(`${key}_title`)), body || createElement('p', {}, this.t(`${key}_body`)));
        this.replaceChildren(box);
      }

      async load() {
        try {
          this.correction = await this.request('/corrections/lookup', { token: this.token });
          this.renderForm();
        } catch (error) {
          this.renderState(error.code === 'correction_expired' ? 'expired' : error.code === 'correction_resolved' ? 'resolved' : 'invalid');
        }
      }

      orderName() {
        return String(this.correction.order_name || this.correction.order_id || '').trim();
      }

      renderForm() {
        const initial = this.correction.purpose === 'initial_submission';
        const fields = Array.isArray(this.correction.fields) ? this.correction.fields : [];
        // Staff chose only "Other" (nothing to enter), or the form is unavailable: point to support.
        if ((!initial && !fields.length) || !this.accountTemplate) {
          this.renderState('contact', supportMessage(createElement('p'), this.t('contact_body', { order: this.orderName() }), { chat: this.t('live_chat_sign_in'), telegram: this.t('telegram_order_number') }));
          return;
        }
        const form = createElement('form', { class: 'eight-bp-correction__form', novalidate: true });
        const header = createElement('header', { class: 'eight-bp-correction__header' });
        if (this.orderName()) header.append(createElement('p', { class: 'eight-bp-correction__order' }, this.t('correction_order', { order: this.orderName() })));
        header.append(
          createElement('h1', { class: 'h3' }, this.t(initial ? 'initial_submission_title' : 'title')),
          createElement('p', {}, this.t(initial ? 'initial_submission_intro' : 'intro'))
        );
        // Listed in the same order as the account form fields.
        const reasons = initial ? [] : REASON_ORDER.filter((key) => fields.includes(key)).map((key) => this.t(`reason_${key}`)).filter(Boolean);
        if (reasons.length) {
          const list = createElement('ul', { class: 'eight-bp-correction__reasons' });
          reasons.forEach((reason) => list.append(createElement('li', {}, reason)));
          header.append(list);
        }
        const context = createElement('p', { class: 'eight-bp-correction__context', hidden: true });
        header.append(context);
        form.append(header);

        const replaceAccount = initial || fields.includes('account_type');
        const accountForm = this.accountTemplate.content.cloneNode(true);
        this.account = accountForm.querySelector('eight-bp-account-input');
        if (!replaceAccount) {
          this.account.dataset.correctionType = this.correction.account_type || '';
          this.account.dataset.correctionFields = fields.join(',');
        }
        this.account.dataset.allowNewAccount = String(Boolean(this.correction.allow_new_account));
        if (this.correction.allow_new_account) {
          // A new account is created for the product the customer bought.
          this.account.dataset.productId = this.correction.product_id || '';
          this.account.dataset.variantId = this.correction.variant_id || '';
        }
        form.append(accountForm);

        this.error = createErrorBox(accountForm);
        const submit = createElement('button', { type: 'submit', class: 'btn btn--primary eight-bp-correction__submit position-relative' });
        const spinner = createElement('span', { class: 'loading-overlay__spinner hidden', 'aria-hidden': 'true' });
        spinner.append(createElement('span', { 'data-load': 'loading' }));
        submit.append(spinner, createElement('span', {}, this.t('submit')));
        submit.firstElementChild.append(createElement('span', { 'data-load': 'loading' }));
        form.append(this.error, submit);
        form.addEventListener('submit', (event) => this.submit(event, submit));
        this.replaceChildren(form);

        // The account form is connected now, so its provider labels are available.
        const masked = this.correction.masked_login_identifier;
        if (!initial && masked) {
          const provider = this.account.providerLabel(this.correction.account_type);
          context.textContent = this.t('account_context', { account: [provider, masked].filter(Boolean).join(' · ') });
          context.hidden = false;
        }
        if (this.account.isCorrection) {
          const switchType = createElement('button', { type: 'button', class: 'btn-link btn-link--sm eight-bp-correction__switch' }, this.t('switch_type'));
          switchType.addEventListener('click', () => {
            switchType.remove();
            this.account.showFullForm();
          });
          this.account.after(switchType);
        }
      }

      setBusy(button, busy) {
        button.disabled = busy;
        button.setAttribute('aria-busy', String(busy));
        button.querySelector('.loading-overlay__spinner')?.classList.toggle('hidden', !busy);
      }

      async submit(event, button) {
        event.preventDefault();
        if (button.disabled) return;
        setErrorBox(this.error, '');
        const payload = { token: this.token };
        try {
          if (this.account.isCorrection) Object.assign(payload, this.account.collectCorrection());
          this.setBusy(button, true);
          if (!this.account.isCorrection) {
            await this.account.prepareForSubmit();
            payload.account_reference = this.account.referenceField.value;
            // Reuse the cart ID the account was created with: with an empty cart, Shopify
            // returns a new ID on every read, so a second read never matches.
            payload.cart_context = this.account._lastCartContext || await this.account.getCartContext();
          }
          await this.request('/corrections/submit', payload);
          this.token = '';
          this.renderState('success');
        } catch (error) {
          setErrorBox(this.error, error.message || this.t('request_failed'));
          this.setBusy(button, false);
        }
      }
    });
  }

  window.EightBpAccountInputs = {
    prepare: async (root) => {
      const components = Array.from(root?.querySelectorAll?.('eight-bp-account-input') || []);
      for (const component of components) await component.prepareForSubmit();
    },
    rebindForCart: async (cartContext, items) => {
      const refs = new Set((items || []).map(item => String(item.properties?._8bp_account_use_ref || '').trim()).filter(Boolean));
      for (const reference of refs) {
        let oldContext = '';
        try { oldContext = localStorage.getItem(`bbirdg:8bp-account-context:${reference}`) || ''; } catch (_) {}
        if (oldContext && oldContext !== cartContext) {
          await fetch(`${API_ROOT}/account-uses/rebind`, { method: 'POST', credentials: 'same-origin', headers: {'Content-Type':'application/json','Accept':'application/json'}, body: JSON.stringify({ reference, old_cart_context: oldContext, cart_context: cartContext }) }).then(async response => { if (!response.ok) throw new Error('Account details must be selected again.'); });
          try { localStorage.setItem(`bbirdg:8bp-account-context:${reference}`, cartContext); } catch (_) {}
        }
      }
    }
  };

  enhanceProductInputs();
})();
